import {
  Component,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
} from "@angular/core";
import { FlatTreeControl } from "@angular/cdk/tree";
import {
  MatTreeFlatDataSource,
  MatTreeFlattener,
} from "@angular/material/tree";
import { Store } from "@ngrx/store";
import { Subscription } from "rxjs";

import { AppState } from "@skills-app/root.reducer";
import IOrgNode from "@skills-app/models/orgNode";
import * as fromConsultantSearchReducer from "@skills-app/consultant-skill/reducers/consultant-search.reducer";
import * as SearchBySkillOrConsultantApiActions from "@skills-app/consultant-skill/actions/search-by-skill-or-consultant-api.actions";
import * as SearchBySkillOrConsultantPageActions from "@skills-app/consultant-skill/actions/search-by-skill-or-consultant.actions";

type OrgNodeWithExpand = IOrgNode & { isExpanded?: boolean };

export class OrgFlatNode {
  name!: string;
  level!: number;
  expandable!: boolean;
  orgNode!: IOrgNode;
}

@Component({
  selector: "app-lob-filter",
  standalone: false,
  templateUrl: "./lob-filter.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./lob-filter.component.css"],
})
export class LobFilterComponent implements OnInit, OnDestroy {
  orgCount = 0;
  flatNodeMap = new Map<OrgFlatNode, IOrgNode>();
  nestedNodeMap = new Map<IOrgNode, OrgFlatNode>();
  selectedParent: OrgFlatNode | null = null;
  treeControl: FlatTreeControl<OrgFlatNode>;
  allDataNodes: OrgFlatNode[] = [];
  treeFlattener: MatTreeFlattener<IOrgNode, OrgFlatNode>;
  dataSource: MatTreeFlatDataSource<IOrgNode, OrgFlatNode>;
  checklistSelection = new OrgNodeSelectionModel();
  organisations: IOrgNode[] = [];
  subscription?: Subscription;
  private didRequestOrgsForce = false;
  private didInitialCollapse = false;
  private didInitialRootSelection = false;
  private currentSelectedOrgIds = new Set<string>();
  private visualSelectedOrgIds = new Set<string>();
  private lastReadySelectionKey = "";

  constructor(private readonly store: Store<AppState>) {
    this.treeFlattener = new MatTreeFlattener<IOrgNode, OrgFlatNode>(
      this.transformer,
      this.getLevel,
      this.isExpandable,
      this.getChildren,
    );

    this.treeControl = new FlatTreeControl<OrgFlatNode>(
      this.getLevel,
      this.isExpandable,
    );

    this.dataSource = new MatTreeFlatDataSource<IOrgNode, OrgFlatNode>(
      this.treeControl,
      this.treeFlattener,
    );
  }

  ngOnInit(): void {
    const orgs$ = this.store.select(fromConsultantSearchReducer.getOrgs);
    const selectedOrgs$ = this.store.select(
      fromConsultantSearchReducer.getSelectedOrgs,
    );

    this.subscription = new Subscription();

    this.subscription.add(
      selectedOrgs$.subscribe((selectedOrgs) => {
        const incomingIds = new Set(
          (selectedOrgs ?? [])
            .map((o) => String(o?.Id ?? "").trim())
            .filter(Boolean),
        );

        // Ignore transient empty emissions once we already have a visual selection
        // and the TE tree is present, otherwise the root checkbox gets visually
        // cleared during replay/rebuild timing.
        if (
          incomingIds.size === 0 &&
          this.visualSelectedOrgIds.size > 0 &&
          this.organisations.length > 0
        ) {
          return;
        }

        this.currentSelectedOrgIds = new Set(incomingIds);

        if (incomingIds.size === 0) {
          this.lastReadySelectionKey = "";
          this.visualSelectedOrgIds = new Set();
        }

        if (this.allDataNodes.length > 0) {
          this.applySelectedIdsToCurrentNodes(this.currentSelectedOrgIds);
          this.treeControl.collapseAll();
          this.emitSelectionReadyIfNeeded();
        }
      }),
    );

    this.subscription.add(
      orgs$.subscribe((orgs) => {
        const safeOrgs = orgs ?? [];

        if (safeOrgs.length === 0 && !this.didRequestOrgsForce) {
          this.didRequestOrgsForce = true;
          this.store.dispatch(
            SearchBySkillOrConsultantApiActions.requestOrgsForce(),
          );
          return;
        }

        const displayOrgs = this.getTeOnlyTree(safeOrgs);

        this.dataSource.data = displayOrgs;
        this.organisations = displayOrgs;
        this.orgCount = this.getOrgNodeList(
          displayOrgs as OrgNodeWithExpand[],
        ).length;

        Promise.resolve().then(() => {
          this.allDataNodes = [...this.treeControl.dataNodes];
          this.checklistSelection.setItems(this.allDataNodes);

          if (!this.didInitialCollapse) {
            this.treeControl.collapseAll();
            this.didInitialCollapse = true;
          }

          if (this.currentSelectedOrgIds.size > 0) {
            this.applySelectedIdsToCurrentNodes(this.currentSelectedOrgIds);
            this.treeControl.collapseAll();
            this.emitSelectionReadyIfNeeded();
            return;
          }

          if (
            !this.didInitialRootSelection &&
            this.organisations.length > 0 &&
            this.currentSelectedOrgIds.size === 0
          ) {
            const root = this.organisations[0];
            const rootFlatNode = this.allDataNodes.find(
              (n) => String(n?.orgNode?.Id ?? "") === String(root?.Id ?? ""),
            );

            if (root?.Id && rootFlatNode) {
              this.didInitialRootSelection = true;

              const descendants = this.getAllDescendants(rootFlatNode);
              const selectedFlatNodes = [rootFlatNode, ...descendants];

              this.checklistSelection.clear();
              this.checklistSelection.select(...selectedFlatNodes);
              this.promoteFullySelectedParents();

              const selectedIds = new Set(
                this.checklistSelection.selected
                  .map((node) => String(node?.orgNode?.Id ?? "").trim())
                  .filter(Boolean),
              );

              this.currentSelectedOrgIds = new Set(selectedIds);
              this.visualSelectedOrgIds = new Set(selectedIds);

              this.treeControl.collapseAll();
              this.emitSelectionReadyIfNeeded();
            }
          }
        });
      }),
    );

    this.store.dispatch(SearchBySkillOrConsultantApiActions.requestOrgsForce());
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  setFilter(): void {
    const normalizedSelectedFlatNodes = this.getNormalizedSelectedFlatNodes(
      this.checklistSelection.selected,
    );

    const selectedOrgs = normalizedSelectedFlatNodes.map(
      (flatNode) => flatNode.orgNode,
    );

    const selectedIds = new Set(
      selectedOrgs.map((o) => String(o?.Id ?? "").trim()).filter(Boolean),
    );

    this.currentSelectedOrgIds = new Set(selectedIds);
    this.visualSelectedOrgIds = new Set(selectedIds);

    this.store.dispatch(
      SearchBySkillOrConsultantPageActions.filterConsultantsByOrg({
        orgs: selectedOrgs,
      }),
    );

    this.store.dispatch(
      SearchBySkillOrConsultantApiActions.requestFilteredConsultants(),
    );
  }

  getLevel = (node: OrgFlatNode): number => node.level;

  isExpandable = (node: OrgFlatNode): boolean => node.expandable;

  getChildren = (node: IOrgNode): IOrgNode[] => node.Children ?? [];

  hasChild = (_index: number, nodeData: OrgFlatNode): boolean =>
    nodeData.expandable;

  hasNoContent = (_index: number, nodeData: OrgFlatNode): boolean =>
    nodeData.orgNode == null;

  transformer = (node: IOrgNode, level: number): OrgFlatNode => {
    const existingNode = this.nestedNodeMap.get(node);
    const flatNode =
      existingNode && existingNode.name === node.Name
        ? existingNode
        : new OrgFlatNode();

    flatNode.name = node.Name;
    flatNode.level = level;
    flatNode.expandable = !!node.Children?.length;
    flatNode.orgNode = node;

    this.flatNodeMap.set(flatNode, node);
    this.nestedNodeMap.set(node, flatNode);

    return flatNode;
  };

  descendantsAllSelected(node: OrgFlatNode): boolean {
    const descendants = this.getDescendants(node);
    return (
      descendants.length > 0 &&
      descendants.every((child) => this.isNodeExplicitlySelected(child))
    );
  }

  descendantsPartiallySelected(node: OrgFlatNode): boolean {
    const descendants = this.getDescendants(node);
    const result = descendants.some((child) =>
      this.isNodeExplicitlySelected(child),
    );
    return result && !this.descendantsAllSelected(node);
  }

  isNodeVisuallySelected(node: OrgFlatNode): boolean {
    if (this.isNodeExplicitlySelected(node)) {
      return true;
    }

    if (this.descendantsAllSelected(node)) {
      return true;
    }

    const normalizedVisualIds = this.normalizeSelectedIds(
      new Set(this.visualSelectedOrgIds),
    );

    const nodeId = String(node?.orgNode?.Id ?? "").trim();
    return !!nodeId && normalizedVisualIds.has(nodeId);
  }

  private isNodeExplicitlySelected(node: OrgFlatNode): boolean {
    const id = String(node?.orgNode?.Id ?? "").trim();
    return (
      (!!id && this.checklistSelection.isSelected(node)) ||
      (!!id && this.visualSelectedOrgIds.has(id))
    );
  }

  orgSelectionToggle(node: OrgFlatNode): void {
    this.checklistSelection.toggle(node);
    const descendants = this.getAllDescendants(node);

    if (this.checklistSelection.isSelected(node)) {
      this.checklistSelection.select(...descendants);
    } else {
      this.checklistSelection.deselect(...descendants);
    }

    this.checkAllParentsSelection(node);
    this.setFilter();
  }

  leafOrgSelectionToggle(node: OrgFlatNode): void {
    this.checklistSelection.toggle(node);
    this.checkAllParentsSelection(node);
    this.setFilter();
  }

  checkAllParentsSelection(node: OrgFlatNode): void {
    let parent: OrgFlatNode | null = this.getParentNode(node);
    while (parent) {
      this.checkRootNodeSelection(parent);
      parent = this.getParentNode(parent);
    }
  }

  checkRootNodeSelection(node: OrgFlatNode): void {
    const descendants = this.getDescendants(node);

    if (descendants.length === 0) {
      return;
    }

    const descAllSelected = descendants.every((child) =>
      this.checklistSelection.isSelected(child),
    );

    if (this.checklistSelection.isSelected(node) && !descAllSelected) {
      this.checklistSelection.deselect(node);
    } else if (!this.checklistSelection.isSelected(node) && descAllSelected) {
      this.checklistSelection.select(node);
    }
  }

  getParentNode(node: OrgFlatNode): OrgFlatNode | null {
    const currentLevel = this.getLevel(node);

    if (currentLevel < 1) {
      return null;
    }

    const startIndex = this.allDataNodes.indexOf(node) - 1;

    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.allDataNodes[i];
      if (this.getLevel(currentNode) < currentLevel) {
        return currentNode;
      }
    }

    return null;
  }

  private getTeOnlyTree(nodes: IOrgNode[]): IOrgNode[] {
    const teNode = this.findTeNode(nodes);
    return teNode ? [teNode] : nodes ?? [];
  }

  private findTeNode(nodes: IOrgNode[]): IOrgNode | null {
    for (const node of nodes ?? []) {
      if (this.isTeNode(node)) {
        return node;
      }

      const childMatch = this.findTeNode(node.Children ?? []);
      if (childMatch) {
        return childMatch;
      }
    }

    return null;
  }

  private isTeNode(node: IOrgNode | null | undefined): boolean {
    const name = (node?.Name ?? "").trim().toLowerCase();
    return name.startsWith("telstra enterprise");
  }

  filterOrganisations(orgs: OrgNodeWithExpand[]): void {
    const safeOrgs = orgs ?? [];

    const selectedIds =
      this.visualSelectedOrgIds.size > 0
        ? new Set(this.visualSelectedOrgIds)
        : this.currentSelectedOrgIds.size > 0
          ? new Set(this.currentSelectedOrgIds)
          : new Set(
              this.checklistSelection.selected
                .map((n) => String(n?.orgNode?.Id ?? "").trim())
                .filter(Boolean),
            );

    this.dataSource.data = safeOrgs;
    this.orgCount = this.getOrgNodeList(safeOrgs).length;

    Promise.resolve().then(() => {
      this.allDataNodes = [...this.treeControl.dataNodes];
      this.checklistSelection.setItems(this.allDataNodes);
      this.applySelectedIdsToCurrentNodes(selectedIds);
      this.treeControl.collapseAll();
      this.emitSelectionReadyIfNeeded();
    });
  }

  private getOrgNodeList(nodes: OrgNodeWithExpand[]): OrgNodeWithExpand[] {
    const orgNodes: OrgNodeWithExpand[] = [];

    (nodes ?? []).forEach((node) => {
      orgNodes.push(node);

      if (node.Children?.length) {
        this.getOrgNodeList(node.Children as OrgNodeWithExpand[]).forEach(
          (child) => orgNodes.push(child),
        );
      }
    });

    return orgNodes;
  }

  private getDescendants(node: OrgFlatNode): OrgFlatNode[] {
    const flatNodes: OrgFlatNode[] = [];
    const startIndex = this.allDataNodes.indexOf(node);
    const level = node.level;

    for (let i = startIndex + 1; i < this.allDataNodes.length; i++) {
      const current = this.allDataNodes[i];
      if (current.level <= level) {
        break;
      }
      flatNodes.push(current);
    }

    return flatNodes;
  }

  private getAllDescendants(node: OrgFlatNode): OrgFlatNode[] {
    return this.getDescendants(node);
  }

  private getVisibleRootFlatNode(): OrgFlatNode | null {
    return this.allDataNodes.find((node) => node.level === 0) ?? null;
  }

  private normalizeSelectedIds(selectedIds: Set<string>): Set<string> {
    const normalized = new Set(selectedIds);
    const rootNode = this.getVisibleRootFlatNode();

    if (!rootNode) {
      return normalized;
    }

    const rootId = String(rootNode?.orgNode?.Id ?? "").trim();
    if (!rootId) {
      return normalized;
    }

    const descendants = this.getAllDescendants(rootNode);
    const descendantIds = descendants
      .map((node) => String(node?.orgNode?.Id ?? "").trim())
      .filter(Boolean);

    const rootExplicitlySelected = normalized.has(rootId);
    const allDescendantsSelected =
      descendantIds.length > 0 &&
      descendantIds.every((id) => normalized.has(id));

    if (!rootExplicitlySelected && !allDescendantsSelected) {
      return normalized;
    }

    normalized.add(rootId);

    for (const descendantId of descendantIds) {
      normalized.add(descendantId);
    }

    return normalized;
  }

  private applySelectedIdsToCurrentNodes(selectedIds: Set<string>): void {
    const normalizedIds = this.normalizeSelectedIds(selectedIds);

    this.checklistSelection.clear();

    for (const node of this.allDataNodes) {
      const id = String(node?.orgNode?.Id ?? "").trim();
      if (normalizedIds.has(id)) {
        this.checklistSelection.select(node);
      }
    }

    this.visualSelectedOrgIds = new Set(normalizedIds);

    this.promoteFullySelectedParents();

    this.currentSelectedOrgIds = new Set(this.visualSelectedOrgIds);
  }

  private promoteFullySelectedParents(): void {
    for (let i = this.allDataNodes.length - 1; i >= 0; i--) {
      const node = this.allDataNodes[i];
      if (node.expandable) {
        this.checkRootNodeSelection(node);
      }
    }
  }

  private getNormalizedSelectedFlatNodes(
    selectedFlatNodes: OrgFlatNode[],
  ): OrgFlatNode[] {
    const selectedIds = new Set(
      (selectedFlatNodes ?? [])
        .map((node) => String(node?.orgNode?.Id ?? "").trim())
        .filter(Boolean),
    );

    const normalizedIds = this.normalizeSelectedIds(selectedIds);

    return this.allDataNodes.filter((node) =>
      normalizedIds.has(String(node?.orgNode?.Id ?? "").trim()),
    );
  }

  private emitSelectionReadyIfNeeded(): void {
    const normalizedSelectedFlatNodes = this.getNormalizedSelectedFlatNodes(
      this.checklistSelection.selected,
    );

    if ((normalizedSelectedFlatNodes?.length ?? 0) === 0) {
      return;
    }

    const selectedOrgs = normalizedSelectedFlatNodes.map(
      (flatNode) => flatNode.orgNode,
    );

    const selectionKey = selectedOrgs
      .map((o) => String(o?.Id ?? "").trim())
      .filter(Boolean)
      .join("|");

    if (!selectionKey.length || selectionKey === this.lastReadySelectionKey) {
      return;
    }

    this.lastReadySelectionKey = selectionKey;
    this.currentSelectedOrgIds = new Set(
      selectedOrgs.map((o) => String(o?.Id ?? "").trim()).filter(Boolean),
    );
    this.visualSelectedOrgIds = new Set(this.currentSelectedOrgIds);

    this.store.dispatch(
      SearchBySkillOrConsultantPageActions.filterConsultantsByOrg({
        orgs: selectedOrgs,
      }),
    );

    this.store.dispatch(
      SearchBySkillOrConsultantPageActions.lobSelectionReadyForSearch(),
    );
  }
}

class OrgNodeSelectionModel {
  private readonly selectedIds = new Set<string>();
  private nodesById = new Map<string, OrgFlatNode>();

  setItems(nodes: OrgFlatNode[]): void {
    this.nodesById = new Map(
      (nodes ?? [])
        .filter((n) => !!n?.orgNode?.Id)
        .map((n) => [String(n.orgNode.Id), n] as const),
    );
  }

  clear(): void {
    this.selectedIds.clear();
  }

  select(...nodes: OrgFlatNode[]): void {
    for (const node of nodes) {
      const id = String(node?.orgNode?.Id ?? "").trim();
      if (id) {
        this.selectedIds.add(id);
      }
    }
  }

  deselect(...nodes: OrgFlatNode[]): void {
    for (const node of nodes) {
      const id = String(node?.orgNode?.Id ?? "").trim();
      if (id) {
        this.selectedIds.delete(id);
      }
    }
  }

  toggle(node: OrgFlatNode): void {
    if (this.isSelected(node)) {
      this.deselect(node);
    } else {
      this.select(node);
    }
  }

  isSelected(node: OrgFlatNode): boolean {
    const id = String(node?.orgNode?.Id ?? "").trim();
    return !!id && this.selectedIds.has(id);
  }

  get selected(): OrgFlatNode[] {
    const nodes: OrgFlatNode[] = [];

    for (const id of this.selectedIds) {
      const node = this.nodesById.get(id);
      if (node) {
        nodes.push(node);
      }
    }

    return nodes;
  }
}
